import { EventObject, PromiseActorLogic, fromPromise } from 'xstate';

export interface ServiceInput<
  TContext = any,
  TEvent extends EventObject = EventObject
> {
  context: TContext;
  event: TEvent;
}

type Service = (context: any, event: any) => unknown;

/**
 * Invoke `input` for actors made with `fromServices`: hands the service the
 * machine context and the event that entered the invoking state, as xstate v4 did.
 */
export const serviceInput = ({
  context,
  event
}: ServiceInput): ServiceInput => ({
  context,
  event
});

/**
 * Wraps xstate v4-style `(context, event) => Promise` services as v5 promise
 * actors. Every invoke using one of them must set `input: serviceInput`.
 */
export const fromServices = <TServices extends Record<string, Service>>(
  services: TServices
) =>
  Object.fromEntries(
    Object.entries(services).map(([name, service]) => [
      name,
      fromPromise(async ({ input }: { input: ServiceInput }) =>
        service(input.context, input.event)
      )
    ])
  ) as {
    [K in keyof TServices]: PromiseActorLogic<
      Awaited<ReturnType<TServices[K]>>,
      ServiceInput
    >;
  };

/**
 * Function that asserts that a given event matches one of the included types.
 * Throws an error if the event type does not match.
 * @param {TE} event an event or object returned from an event
 * @param {...TType} eventTypes list of acceptable event types
 *
 */
export function assertEventType<
  TE extends EventObject,
  TType extends TE['type']
>(event: TE, ...eventTypes: TType[]): asserts event is TE & { type: TType } {
  if (!eventTypes.includes(event.type as TType)) {
    throw new Error(
      `Invalid event: expected one of ["${eventTypes.join('", "')}"], got "${
        event.type
      }"`
    );
  }
}
